import React from "react";
import { Box, Grid, Chip } from "@mui/material";
import {
  Person,
  DirectionsCarOutlined,
  CheckCircleOutlined,
  CancelOutlined,
  DateRangeOutlined,
  InfoOutlined,
} from "@mui/icons-material";

import InformationTile from "components/Shared/InformationTile";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import { localizeDateTimeShort } from "utils/dateFormats";
import { useDriverTransferRequestDetailContext } from "contexts/DriverTransferRequestDetailContext";

const STATUS_STYLES = {
  pending: { backgroundColor: "#FEF9E7", textColor: "#D4AC0D" },
  approved: { backgroundColor: "#EBF5FB", textColor: "#1A7A1A" },
  rejected: { backgroundColor: "#FDEDEC", textColor: "#E74C3C" },
};

const getStatusStyle = (status) =>
  STATUS_STYLES[status?.toLowerCase()] ?? {
    backgroundColor: "#F8F9F9",
    textColor: "#7F8C8D",
  };

const DriverTransferRequestBasicDetail = () => {
  const { transferRequestDetail, loadingTransferRequest } =
    useDriverTransferRequestDetailContext();

  if (loadingTransferRequest) return <InfoGridSkeleton />;

  const status = transferRequestDetail?.status;
  const { backgroundColor, textColor } = getStatusStyle(status);

  return (
    <Box padding={3}>
      <Grid container spacing={4}>
        {/* From Driver */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <Person color="primary" />}
            title="From Driver"
            subtitle={
              transferRequestDetail?.fromDriverId?.name ||
              transferRequestDetail?.fromDriverId?.driverId ||
              "N/A"
            }
          />
        </Grid>

        {/* To Driver */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <Person color="primary" />}
            title="To Driver"
            subtitle={
              transferRequestDetail?.toDriverId?.name ||
              transferRequestDetail?.toDriverId?.driverId ||
              "N/A"
            }
          />
        </Grid>

        {/* From Vehicle */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <DirectionsCarOutlined color="primary" />}
            title="From Vehicle"
            subtitle={
              transferRequestDetail?.fromVehicleId?.registration_number ||
              transferRequestDetail?.fromVehicleId?.vehicle_id ||
              "N/A"
            }
          />
        </Grid>

        {/* To Vehicle */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <DirectionsCarOutlined color="primary" />}
            title="To Vehicle"
            subtitle={
              transferRequestDetail?.toVehicleId?.registration_number ||
              transferRequestDetail?.toVehicleId?.vehicle_id ||
              "N/A"
            }
          />
        </Grid>

        {/* Status */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Status"
            subtitle={
              <Box display="flex" alignItems="start" mt={1}>
                <Chip
                  label={status ?? "Unknown"}
                  size="small"
                  sx={{
                    backgroundColor,
                    color: textColor,
                    fontWeight: "bold",
                    textTransform: "capitalize",
                    fontSize: "0.7rem",
                    height: "20px",
                    px: "6px",
                    borderRadius: "12px",
                  }}
                />
              </Box>
            }
          />
        </Grid>

        {/* Approved At */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <CheckCircleOutlined color="primary" />}
            title="Approved At"
            subtitle={
              transferRequestDetail?.approvedAt
                ? localizeDateTimeShort(transferRequestDetail.approvedAt)
                : "N/A"
            }
          />
        </Grid>

        {/* Rejected At */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <CancelOutlined color="primary" />}
            title="Rejected At"
            subtitle={
              transferRequestDetail?.rejectedAt
                ? localizeDateTimeShort(transferRequestDetail.rejectedAt)
                : "N/A"
            }
          />
        </Grid>

        {/* Created At */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <DateRangeOutlined color="primary" />}
            title="Created At"
            subtitle={
              transferRequestDetail?.createdAt
                ? localizeDateTimeShort(transferRequestDetail.createdAt)
                : "N/A"
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default DriverTransferRequestBasicDetail;